import { type PathParams } from "@constellation-academy/epic-ui";
import { Button } from "primereact/button";
import { Card } from "primereact/card";
import { Suspense, useState } from "react";
import { useLazyLoadQuery, useMutation, usePaginationFragment } from "react-relay";
import { useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "@i18n/hooks";
import { type accountEditScreen_AccountFragment$key } from "@relay/accountEditScreen_AccountFragment.graphql";
import { type accountEditScreen_AccountGroupsQuery } from "@relay/accountEditScreen_AccountGroupsQuery.graphql";
import { type accountEditScreen_CreateMutation } from "@relay/accountEditScreen_CreateMutation.graphql";
import { type accountEditScreen_Query } from "@relay/accountEditScreen_Query.graphql";
import { type AccountEditScreen_Refetch } from "@relay/AccountEditScreen_Refetch.graphql";
import { type accountEditScreen_UpdateMutation } from "@relay/accountEditScreen_UpdateMutation.graphql";
import {
	ACCOUNT_FRAGMENT,
	ACCOUNT_GROUP_QUERY,
	ACCOUNT_QUERY,
	CREATE_MUTATION,
	UPDATE_MUTATION,
} from "./account-edit.graphql";
import { StyledDialog } from "./account-edit.styles";
import { AddUserToAccount } from "./parts/add-user-to-account";
import { EditAccountForm } from "./parts/edit-account-form";
import { type EditAccountFormState } from "./parts/edit-account-form/edit-account-form.types";
import { EditLeadStrategyForm } from "./parts/edit-lead-strategy-form";
import { BaseScreen } from "../../components/base-screen";
import { NoAccess } from "../../components/no-access";
import { EditAccountGroupsButton } from "../../features/accounts/edit-account-groups-button/edit-account-groups-button.component";
import { UsersInAccountTable } from "../../features/accounts/users-in-account-table";
import { AccountLicensesTable } from "../../features/licenses/account-licenses-table";
import { ManualOrdersTable } from "../../features/orders/manual-orders-table";
import { OrdersTable } from "../../features/orders/orders-table/orders-table.component";
import { useHasPermissions } from "../../hooks/use-has-permissions";
import { Paths } from "../../routes/paths";
import { type AccountsPath } from "../accounts";

export const AccountEditScreen = () => {
	const { t } = useTranslation("common");

	const { accountId } = useParams<PathParams<typeof AccountsPath>>();
	const isCreatingAccount = accountId === "new";
	const query = useLazyLoadQuery<accountEditScreen_Query>(
		ACCOUNT_QUERY,
		{
			id: accountId ?? "",
			skip: !accountId || isCreatingAccount,
		},
		{ fetchPolicy: "network-only" },
	);

	const accountGroupsQuery = useLazyLoadQuery<accountEditScreen_AccountGroupsQuery>(
		ACCOUNT_GROUP_QUERY,
		{},
	);

	const {
		hasNext,
		loadNext,
		data: account,
	} = usePaginationFragment<AccountEditScreen_Refetch, accountEditScreen_AccountFragment$key>(
		ACCOUNT_FRAGMENT,
		query.node ?? null,
	);

	const [create, isCreating] = useMutation<accountEditScreen_CreateMutation>(CREATE_MUTATION);
	const [update, isUpdating] = useMutation<accountEditScreen_UpdateMutation>(UPDATE_MUTATION);

	const navigate = useNavigate();

	const [addUserDialogVisible, setAddUserDialogVisible] = useState(false);

	const handleLoadNextOnClick = () => {
		loadNext(20);
	};

	const handleAddUserOnClick = () => {
		setAddUserDialogVisible(true);
	};

	const handleAddUserDialogOnHide = () => {
		setAddUserDialogVisible(false);
	};

	const handleOnUserAdded = () => {
		setAddUserDialogVisible(false);
	};

	const handleEditAccountBaseDataOnClick = () => {
		navigate(Paths.accounts.withId(accountId ?? "").edit.basedata.path);
	};

	const connections = account?.extension?.users?.__id ? [account?.extension.users?.__id] : [];

	const title = isCreatingAccount
		? t("accounts.createTitle")
		: t("accounts.editTitle", { name: account?.name });

	const updateAccount = (values: EditAccountFormState) => {
		if (!account?.id) return;
		update({
			variables: {
				input: {
					id: account.id,
					name: values.name!,
				},
			},
		});
	};

	const createAccount = (values: EditAccountFormState) => {
		create({
			variables: {
				input: {
					name: values.name!,
				},
			},
			onCompleted: (response) => {
				const accountId = response.Admin.Auth.createAccount?.edge.node.id;
				accountId &&
					navigate(Paths.accounts.withId(accountId).edit.path, { replace: true });
			},
		});
	};

	const handleAccountFormOnSubmit = (values: EditAccountFormState) => {
		if (account) {
			updateAccount(values);
		} else {
			createAccount(values);
		}
	};

	const canViewOrders = useHasPermissions(["UserInAccountPermission_OrderAdmin_Read"]);

	const canModifyOrders = useHasPermissions(["UserInAccountPermission_OrderAdmin_Modify"]);

	const canReadAuth = useHasPermissions(["UserInAccountPermission_AuthAdmin_Read"]);

	const canModifyAuth = useHasPermissions(["UserInAccountPermission_AuthAdmin_Modify"]);

	const canReadLicenses = useHasPermissions(["UserInAccountPermission_LicenseAdmin_Read"]);

	const accountGroupsFragmentsRefs =
		accountGroupsQuery.Admin.Management.AccountGroups.edges
			?.map((e) => e?.node)
			.filter((node): node is NonNullable<typeof node> => node != null) ?? [];

	const accountGroups = account?.groupAssociations?.map((group) => group.group?.name).join(", ");

	if (!canReadAuth || !canModifyAuth) {
		return <NoAccess />;
	}

	return (
		<BaseScreen title={title}>
			<div className="max-w-30rem flex flex-col align-items-end gap-4">
				<EditAccountForm
					initialValues={{
						name: account?.name,
					}}
					loading={isCreating || isUpdating}
					onSubmit={handleAccountFormOnSubmit}
				/>
			</div>
			{account && accountGroupsFragmentsRefs && (
				<div className="flex flex-col align-items-center mt-4 gap-4">
					<span>
						<b>{t("accounts.permissions")}</b>
					</span>
					<span>{accountGroups}</span>
					<EditAccountGroupsButton
						accountFragmentRef={account}
						accountGroupsFragmentsRef={accountGroupsFragmentsRefs}
					/>
				</div>
			)}
			{account && (
				<div className="mt-4">
					<Button
						label={t("accounts.editBaseData")}
						onClick={handleEditAccountBaseDataOnClick}
					/>
				</div>
			)}
			{account && (
				<Card className="mt-4" title={t("accounts.accountType")}>
					<EditLeadStrategyForm accountFragmentRef={account} />
				</Card>
			)}

			{account?.extension?.users && (
				<Card className="mt-6" title={t("accounts.users.title")}>
					<div className="flex justify-content-end align-items-center">
						<Button label={t("accounts.users.add")} onClick={handleAddUserOnClick} />
					</div>
					<Suspense fallback={<div>{t("accounts.users.loading")}</div>}>
						<UsersInAccountTable
							accountId={accountId!}
							connections={connections}
							hasNext={hasNext}
							loadNext={handleLoadNextOnClick}
							userInAccountConnectionRef={account.extension.users}
						/>
					</Suspense>
				</Card>
			)}
			{!isCreatingAccount && canViewOrders && (
				<Card className="mt-6" title={t("accounts.orders.title")}>
					<OrdersTable
						ordersFragmentRef={query}
						ordersPerPage={10}
						enableFilter={false}
						canModifyOrders={canModifyOrders}
					/>
				</Card>
			)}
			{!isCreatingAccount && canViewOrders && (
				<Card className="mt-6" title={t("accounts.orders.manualTitle")}>
					<ManualOrdersTable
						manualOrdersFragmentRef={query}
						manualOrdersPerPage={10}
						accountId={accountId!}
						canCreateManualOrders={canModifyOrders}
					/>
				</Card>
			)}
			{!isCreatingAccount && canReadLicenses && accountId && (
				<Card className="mt-6" title={t("accounts.licenses.title")}>
					<AccountLicensesTable accountId={accountId} showLicenseDeleteButton />
				</Card>
			)}
			<StyledDialog visible={addUserDialogVisible} onHide={handleAddUserDialogOnHide}>
				<AddUserToAccount
					accountId={accountId!}
					connections={connections}
					onUserAdded={handleOnUserAdded}
				/>
			</StyledDialog>
		</BaseScreen>
	);
};
